import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react";

const BOOKING_SRC =
  "https://api.leadconnectorhq.com/widget/booking/EWsZL8c4QgnTJHBkJ0XT";
const FORM_EMBED_SRC = "https://api.leadconnectorhq.com/js/form_embed.js";

const FEATURES = [
  {
    icon: "solar:layers-minimalistic-bold-duotone",
    title: "Menu Customization",
    desc: "Rename, reorder, hide and re-skin every CRM menu without touching code.",
  },
  {
    icon: "solar:palette-bold-duotone",
    title: "Theme Builder",
    desc: "Match your brand with custom colors, logos and a fully white-labelled UI.",
  },
  {
    icon: "solar:book-2-bold-duotone",
    title: "Knowledgebase",
    desc: "Publish help articles and guides your sub-accounts can browse in-app.",
  },
  {
    icon: "solar:chat-round-dots-bold-duotone",
    title: "Support App",
    desc: "Give clients an embedded support channel right inside the CRM.",
  },
  {
    icon: "solar:map-point-add-bold-duotone",
    title: "Location Creator",
    desc: "Spin up new sub-accounts from reusable templates with users and settings pre-loaded.",
  },
  {
    icon: "solar:routing-2-bold-duotone",
    title: "Location Initiator",
    desc: "Automate onboarding so every new location is configured the moment it goes live.",
  },
  {
    icon: "solar:clipboard-text-bold-duotone",
    title: "Custom Value Form",
    desc: "Collect and sync custom values across sub-accounts with branded intake forms.",
  },
];

const Demo = () => {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const bookingRef = useRef(null);

  // Inject GoHighLevel's form_embed.js once — it auto-resizes the booking iframe.
  useEffect(() => {
    const existing = document.querySelector(`script[src="${FORM_EMBED_SRC}"]`);
    if (existing) {
      setScriptLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.src = FORM_EMBED_SRC;
    script.type = "text/javascript";
    script.async = true;
    script.onload = () => setScriptLoaded(true);
    document.body.appendChild(script);
  }, []);

  const scrollToBooking = () => {
    bookingRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="container-fluid px-4 py-4">
      <style>{`
        .demo-hero {
          position: relative;
          overflow: hidden;
          border-radius: 20px;
          padding: 48px 40px;
          background: linear-gradient(135deg, #1d4ed8 0%, #2563eb 45%, #7c3aed 100%);
          color: #fff;
        }
        .demo-hero::after {
          content: "";
          position: absolute;
          top: -80px;
          right: -60px;
          width: 280px;
          height: 280px;
          background: rgba(255,255,255,.12);
          border-radius: 50%;
        }
        .demo-hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 14px;
          border-radius: 999px;
          background: rgba(255,255,255,.16);
          font-size: 12.5px;
          font-weight: 600;
          letter-spacing: .3px;
        }
        .demo-hero h3 {
          font-size: 32px;
          font-weight: 700;
          margin: 16px 0 10px;
          line-height: 1.25;
        }
        .demo-hero p {
          font-size: 15px;
          max-width: 560px;
          opacity: .92;
          margin-bottom: 24px;
        }
        .demo-hero-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 22px;
          border-radius: 12px;
          background: #fff;
          color: #1d4ed8;
          font-weight: 600;
          font-size: 14px;
          border: none;
          cursor: pointer;
          transition: transform .15s ease, box-shadow .15s ease;
        }
        .demo-hero-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 28px rgba(0,0,0,.22);
        }
        .demo-section-head {
          margin: 32px 0 14px;
        }
        .demo-section-head h5 {
          font-size: 18px;
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 2px;
        }
        .demo-section-head p {
          font-size: 13px;
          color: #64748b;
          margin: 0;
        }
        .demo-feature-card {
          height: 100%;
          background: #fff;
          border: 1px solid #eef2f7;
          border-radius: 16px;
          padding: 22px;
          transition: transform .15s ease, box-shadow .15s ease;
        }
        .demo-feature-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 14px 30px rgba(15,23,42,.08);
        }
        .demo-feature-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          color: #2563eb;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          margin-bottom: 14px;
        }
        .demo-feature-card h6 {
          font-size: 15px;
          font-weight: 600;
          color: #0f172a;
          margin-bottom: 6px;
        }
        .demo-feature-card p {
          font-size: 13px;
          color: #64748b;
          margin: 0;
        }

        .demo-booking-card {
          background: #fff;
          border: 1px solid #eef2f7;
          border-radius: 18px;
          overflow: hidden;
        }
        .demo-booking-head {
          padding: 22px 24px;
          border-bottom: 1px solid #eef2f7;
        }
        .demo-booking-head h5 {
          font-size: 17px;
          font-weight: 600;
          color: #0f172a;
          margin-bottom: 4px;
        }
        .demo-booking-head p {
          font-size: 13px;
          color: #64748b;
          margin: 0;
        }
        .demo-booking-body {
          position: relative;
          min-height: 720px;
        }
        .demo-booking-loader {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 14px;
          background: #fff;
          z-index: 2;
          transition: opacity .3s ease;
        }
        .demo-booking-loader.hidden {
          opacity: 0;
          pointer-events: none;
        }
        .demo-spinner {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          border: 3px solid #e2e8f0;
          border-top-color: #2563eb;
          animation: demo-spin .8s linear infinite;
        }
        @keyframes demo-spin { to { transform: rotate(360deg); } }
        .demo-loader-hint {
          font-size: 12.5px;
          color: #64748b;
        }
        .demo-booking-iframe {
          width: 100%;
          min-height: 720px;
          border: none;
          overflow: hidden;
          display: block;
        }
      `}</style>

      {/* Hero */}
      <div className="demo-hero">
        <span className="demo-hero-badge">
          <Icon icon="solar:star-bold" /> CRM Customizer
        </span>
        <h3>See the CRM Customizer in action</h3>
        <p>
          White-label GoHighLevel, restyle menus, build themes and ship a
          branded experience for every sub-account. Book a live walkthrough and
          we'll tailor it to your agency.
        </p>
        <button className="demo-hero-btn" onClick={scrollToBooking}>
          <Icon icon="solar:calendar-bold" /> Book a demo
        </button>
      </div>

      {/* Feature highlights */}
      <div className="demo-section-head">
        <h5>Everything in the CRM Customizer</h5>
        <p>A full white-label toolkit for your GoHighLevel agency.</p>
      </div>
      <div className="row g-3">
        {FEATURES.map((feature) => (
          <div className="col-12 col-sm-6 col-xl-3" key={feature.title}>
            <div className="demo-feature-card">
              <span className="demo-feature-icon">
                <Icon icon={feature.icon} />
              </span>
              <h6>{feature.title}</h6>
              <p>{feature.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Calendar booking */}
      <div className="demo-section-head">
        <h5>Schedule your personalized demo</h5>
        <p>Pick a time that works for you — the session takes about 30 minutes.</p>
      </div>
      <div className="demo-booking-card" ref={bookingRef}>
        <div className="demo-booking-head">
          <h5>Book a live walkthrough</h5>
          <p>We'll tailor the session to your agency's setup and questions.</p>
        </div>
        <div className="demo-booking-body">
          {!scriptLoaded && (
            <div className="demo-booking-loader">
              <div className="demo-spinner" />
              <div className="demo-loader-hint">Loading available times…</div>
            </div>
          )}
          <iframe
            title="CRM Customizer Demo Booking"
            src={BOOKING_SRC}
            className="demo-booking-iframe"
            scrolling="no"
            id="EWsZL8c4QgnTJHBkJ0XT_demo"
          ></iframe>
        </div>
      </div>
    </div>
  );
};

export default Demo;
